import { useCallback, useEffect, useRef } from 'react';
import { getVideoGenerationTask } from '../api/seedance';
import { useSeedanceAuthStore } from '../store/seedanceAuthStore';
import { useSeedanceTaskStore } from '../store/seedanceTaskStore';
import type { Task } from '../store/seedanceTypes';
import { useSeedanceUiStore } from '../store/seedanceUiStore';
import { VIDEO_GENERATION_UPSTREAM_BASE_URL } from '../utils/seedanceConstants';
import {
  extractPrompt,
  isUsableVideoUrl,
  mergeTaskTimestamps,
  normalizeStatus,
} from '../utils/seedanceTaskUtils';

const POLL_INTERVAL_MS = 10_000;

/**
 * 注册视频任务状态轮询（须在 Seedance 布局层调用，保证工作台提交后也能轮询）。
 * 任务页可复用返回的 fetchTaskDetail。
 */
export function useSeedanceTaskPolling() {
  const apiKey = useSeedanceAuthStore((s) => s.apiKey);
  const apiKeyRef = useRef(apiKey);
  useEffect(() => {
    apiKeyRef.current = apiKey;
  }, [apiKey]);

  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const setTasks = useSeedanceTaskStore((s) => s.setTasks);
  const setHasUnreadSucceeded = useSeedanceTaskStore((s) => s.setHasUnreadSucceeded);
  const setEnsurePolling = useSeedanceTaskStore((s) => s.setEnsurePolling);
  const pollingRef = useRef<Record<string, number>>({});

  const fetchTaskDetail = useCallback(
    async (taskId: string): Promise<Partial<Task> | null> => {
      try {
        const response = await getVideoGenerationTask(taskId, {
          baseUrl: VIDEO_GENERATION_UPSTREAM_BASE_URL,
          apiKey: apiKeyRef.current,
        });
        addDebug(
          `GET ${VIDEO_GENERATION_UPSTREAM_BASE_URL}/seedance/api/v3/contents/generations/tasks/${taskId}`,
        );
        const data = response?.data && typeof response.data === 'object' ? response.data : response;
        const taskData = data?.data && typeof data.data === 'object' ? data.data : {};
        const rawStatus = data.status || taskData.status;
        let videoUrl: string | null = null;
        let thumbUrl: string | null = null;
        const output = data.output || data.result || {};
        if (isUsableVideoUrl(output.video_url)) videoUrl = output.video_url;
        if (isUsableVideoUrl(output.result_url)) videoUrl = output.result_url;
        if (isUsableVideoUrl(output.url)) videoUrl = output.url;
        if (output.thumbnail_url) thumbUrl = output.thumbnail_url;
        if (data.content) {
          if (Array.isArray(data.content)) {
            data.content.forEach((c: { type?: string; video_url?: { url?: string } }) => {
              if (c.type === 'video_url' && isUsableVideoUrl(c.video_url?.url)) {
                videoUrl = c.video_url!.url!;
              }
            });
          } else if (typeof data.content === 'object') {
            const content = data.content as Record<string, unknown>;
            if (isUsableVideoUrl(content.video_url as string)) videoUrl = content.video_url as string;
            if (isUsableVideoUrl(content.result_url as string)) videoUrl = content.result_url as string;
          }
        }
        if (isUsableVideoUrl(data.result_url)) videoUrl = data.result_url;
        if (isUsableVideoUrl(data.video_url)) videoUrl = data.video_url;
        if (isUsableVideoUrl(taskData.result_url)) videoUrl = taskData.result_url;
        if (isUsableVideoUrl(taskData.video_url)) videoUrl = taskData.video_url;

        let status = normalizeStatus(rawStatus);
        const progressText = String(data.progress || taskData.progress || '');
        const failReason =
          (taskData as { fail_reason?: string }).fail_reason ||
          (taskData as { error?: { message?: string } }).error?.message;
        if (status === 'pending' && (failReason || /fail|error/i.test(String(data.result_url)))) {
          status = 'failed';
        } else if (status === 'pending' && (videoUrl || progressText === '100%')) {
          status = videoUrl ? 'succeeded' : 'failed';
        }
        addDebug(`任务状态: ${rawStatus || 'unknown'} -> ${status}`);

        const detail: Partial<Task> = {
          id: data.id || data.task_id || taskId,
          prompt: extractPrompt(data),
          status,
          video_url: videoUrl,
          thumbnail_url: thumbUrl,
          error: failReason || data.error?.message || data.error || null,
        };
        const createdIso = mergeTaskTimestamps(
          data.created_at,
          data.create_time,
          taskData.created_at,
          taskData.create_time,
        );
        if (createdIso) detail.created_at = createdIso;

        const updatedIso = mergeTaskTimestamps(data.updated_at, taskData.updated_at, data.modified_at);
        if (updatedIso) detail.updated_at = updatedIso;

        const finishedIso = mergeTaskTimestamps(
          data.finished_at,
          taskData.finished_at,
          data.finish_time,
          taskData.finish_time,
        );
        if (finishedIso) detail.finished_at = finishedIso;

        const completedIso = mergeTaskTimestamps(
          data.completed_at,
          taskData.completed_at,
          data.finish_time,
          taskData.finish_time,
        );
        if (completedIso) detail.completed_at = completedIso;

        return detail;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`获取任务 ${taskId} 详情失败: ${msg}`, true);
        return null;
      }
    },
    [addDebug],
  );

  const pollTaskStatus = useCallback(
    async (taskId: string) => {
      const detail = await fetchTaskDetail(taskId);
      if (!detail) return;
      setTasks((prev) => {
        const idx = prev.findIndex((t) => t.id === taskId);
        if (idx === -1) return prev;
        const lp = prev[idx].prompt;
        const lc = prev[idx].context;
        const merged = { ...prev[idx], ...detail } as Task;
        if (lp) merged.prompt = lp;
        if (lc) merged.context = lc;
        const next = prev.slice();
        next[idx] = merged;
        return next;
      });
      if (detail.status === 'succeeded' || detail.status === 'failed') {
        if (pollingRef.current[taskId]) {
          window.clearInterval(pollingRef.current[taskId]);
          delete pollingRef.current[taskId];
        }
        if (detail.status === 'succeeded') {
          showToast(`任务 ${taskId.slice(-8)} 生成完成!`, 's');
          setHasUnreadSucceeded(true);
        } else {
          showToast(`任务 ${taskId.slice(-8)} 失败: ${detail.error || '未知错误'}`, 'e');
        }
      }
    },
    [fetchTaskDetail, setHasUnreadSucceeded, setTasks, showToast],
  );

  const ensurePolling = useCallback(
    (taskId: string) => {
      const id = taskId.trim();
      if (!id || pollingRef.current[id]) return;
      addDebug(`开始轮询任务状态: ${id}`);
      void pollTaskStatus(id);
      pollingRef.current[id] = window.setInterval(() => pollTaskStatus(id), POLL_INTERVAL_MS);
    },
    [addDebug, pollTaskStatus],
  );

  useEffect(() => {
    setEnsurePolling(ensurePolling);
    return () => {
      Object.values(pollingRef.current).forEach((timerId) => window.clearInterval(timerId));
      pollingRef.current = {};
      setEnsurePolling(() => {});
    };
  }, [ensurePolling, setEnsurePolling]);

  return { fetchTaskDetail, ensurePolling };
}
